import BossModel from "@/models/BossModel";
import Proposal from "@/models/Proposal/Proposal";
import Customer from "@/models/Customer/Customer";
import Country from "@/models/Country/Country";
import User from "@/models/User/User";
import LeadChannel from "@/models/Dossier/LeadChannel/LeadChannel";
import ManualDownload from "@/models/Dossier/ManualDownload/ManualDownload";
import CancelReason from "@/models/Dossier/CancelReason/CancelReason";
import Traveller from "@/models/Dossier/Traveller/Traveller";
import Message from "@/models/Message/Message";
import SendMail from "@/models/Dossier/SendMail/SendMail";
import Task from "@/models/Task/Task";
import Payment from "@/models/Dossier/Payment/Payment";
import RevenueGroup from "@/models/RevenueGroup/RevenueGroup";
import DebitInvoiceMismatch from "@/models/Dossier/InvoiceMismatch/DebitInvoiceMismatch";
import CreditInvoiceMismatch from "@/models/Dossier/InvoiceMismatch/CreditInvoiceMismatch";
import constants from "@/constants";
import PhoneCall from "@/models/PhoneCall/PhoneCall";
import Lead from "@/models/Lead/Lead";
import Dialogue from "@/models/Customer/Dialogue/Dialogue";
import orderBy from "lodash/orderBy";
import PhoneSms from "@/models/PhoneSms/PhoneSms";
import Deadline from "@/models/Dossier/Deadline/Deadline";
import AgentProduct from "@/models/Agent/Product/Product";
import AgentBypass from "@/models/Dossier/AgentBypass/AgentBypass";
import Complaint from "@/models/Dossier/Complaint/Complaint";
import BackofficeMessage from "@/models/Dossier/BackofficeMessage/BackofficeMessage";
import Dataset from "@/models/Dataset/Dataset.js";
import Reservation from "@/models/Agent/Reservation/Reservation.js";

export default class Dossier extends BossModel {
    /**
     *
     * @type {string}
     */
    static apiUrl = "dossier";

    /**
     *
     * @type {string}
     */
    static entity = "dossiers";

    static fields() {
        return {
            id: this.number(null).nullable(),
            view_online_token: this.attr(null).nullable(),
            public_id: this.attr(null),
            exchange_rate_id: this.attr(null),
            company_conditions_accepted_at: this.attr(null),
            anvr_conditions_accepted_at: this.attr(null),
            revenue_group_id: this.number(null).nullable(),
            customer_id: this.attr(null),
            user_id: this.attr(null),
            lead_opportunity_cid: this.attr(null),
            service_label_cid: this.attr(null),
            trip_country_id: this.attr(null),
            dossier_cancel_reason_id: this.attr(null),
            dossier_lead_channel_id: this.attr(null),
            is_from_prosale: this.attr(null),
            prosale_eid: this.attr(null),
            released_by: this.attr(null),
            deleted_at: this.attr(null),
            created_at: this.attr(null),
            trip_starts_at: this.attr(null),
            trip_ends_at: this.attr(null),
            is_account_mail_send: this.attr(null),
            is_cancellation_confirmed: this.attr(null),
            cancelled_at: this.attr(null),
            updated_at: this.attr(null),
            send_aftersales_mail: this.attr(null),
            travel_insurance_cid: this.attr(null),
            travel_insurance_polis_number: this.attr(null),
            cancellation_insurance_cid: this.attr(null),
            cancellation_insurance_polis_number: this.attr(null),
            unchecked_agent_product_count: this.number(null),
            unchecked_agent_attachment_download_count: this.number(null),
            price_guaranteed_till: this.attr(null).nullable(),
            dialogues: this.hasMany(Dialogue, "dossier_id"),
            lead: this.hasOne(Lead, "dossier_id"),
            revenueGroup: this.belongsTo(RevenueGroup, "revenue_group_id"),
            customer: this.belongsTo(Customer, "customer_id"),
            user: this.belongsTo(User, "user_id"),
            leadChannel: this.belongsTo(LeadChannel, "dossier_lead_channel_id"),
            cancelReason: this.belongsTo(
                CancelReason,
                "dossier_cancel_reason_id",
            ),
            creditMismatches: this.hasMany(CreditInvoiceMismatch, "dossier_id"),
            debitMismatches: this.hasMany(DebitInvoiceMismatch, "dossier_id"),
            manualDownloads: this.hasMany(ManualDownload, "dossier_id"),
            payments: this.hasMany(Payment, "dossier_id"),
            phoneCalls: this.hasMany(PhoneCall, "dossier_id"),
            phoneSmses: this.hasMany(PhoneSms, "dossier_id"),
            travellers: this.hasMany(Traveller, "dossier_id"),
            activeTravellers: this.hasMany(Traveller, "dossier_id"),
            proposal: this.hasOne(Proposal, "dossier_id"),
            proposals: this.hasMany(Proposal, "dossier_id"),
            allProposals: this.hasMany(Proposal, "dossier_id"),
            archivedProposals: this.hasMany(Proposal, "dossier_id"),
            proposalsWithPrices: this.hasMany(Proposal, "dossier_id"),
            messages: this.hasMany(Message, "dossier_id"),
            tasks: this.hasMany(Task, "dossier_id"),
            openTasks: this.hasMany(Task, "dossier_id"),
            agentProducts: this.hasMany(AgentProduct, "dossier_id"),
            country: this.belongsTo(Country, "trip_country_id"),
            agentBypass: this.hasOne(AgentBypass, "dossier_id"),
            sendMails: this.hasMany(SendMail, "dossier_id"),
            currentExecuteOnDeadline: this.hasOne(Deadline, "dossier_id"),
            currentSendProposalOnDeadline: this.hasOne(Deadline, "dossier_id"),
            currentCallBackOnDeadline: this.hasOne(Deadline, "dossier_id"),
            lastActiveComplaint: this.hasOne(Complaint, "dossier_id"),
            lastBackofficeMessage: this.hasOne(BackofficeMessage, "dossier_id"),
            reservations: this.hasMany(Reservation, "dossier_id"),
        };
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    static exportLeadHistory(params = {}) {
        return this.api()
            .action("stats/lead/history/export")
            .filtered(params.filters)
            .getBlob(false);
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    static getLeadHistory(params = {}) {
        return this.api()
            .action("stats/lead/history")
            .filtered(params.filters)
            .getSingle(false);
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    static getCountryLeadHistory(params = {}) {
        return this.api()
            .action("stats/lead/country/history")
            .filtered(params.filters)
            .getSingle(false);
    }

    /**
     *
     * @param id
     * @param data
     * @returns {Promise<AxiosResponse<T>>}
     */
    static update(id, data) {
        return this.api().updateById(id, data);
    }

    CurrentExecuteOnDeadline() {
        return Deadline.query()
            .where("dossier_id", this.id)
            .where("deadline_type_cid", Deadline.EXECUTE_ON)
            .orderBy("id", "desc")
            .first();
    }

    CurrentCallBackOnDeadline() {
        return Deadline.query()
            .where("dossier_id", this.id)
            .where("deadline_type_cid", Deadline.CALL_BACK_ON)
            .orderBy("id", "desc")
            .first();
    }

    CurrentSendProposalOnDeadline() {
        return Deadline.query()
            .where("dossier_id", this.id)
            .where("deadline_type_cid", Deadline.SEND_PROPOSAL_ON)
            .orderBy("id", "desc")
            .first();
    }

    /**
     *
     * @param dossierCancelReasonId
     * @returns {Promise<AxiosResponse<T>>}
     */
    Cancel(dossierCancelReasonId = null) {
        return this.constructor
            .api()
            .action(this.id + "/cancel")
            .update({ dossier_cancel_reason_id: dossierCancelReasonId });
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    deCancel() {
        return this.constructor
            .api()
            .action(this.id + "/de-cancel")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    ConfirmCancellation() {
        return this.constructor
            .api()
            .action(this.id + "/confirm-cancellation")
            .update();
    }

    /**
     *
     * @param customerId
     * @returns {Promise<AxiosResponse<T>>}
     */
    ChangeCustomer(customerId) {
        return this.constructor
            .api()
            .action(this.id + "/" + customerId + "/change-customer")
            .update([], false)
            .then((response) => {
                new Customer(response).commit();
                this.customer_id = response.id;
                this.commit();
            });
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    SendAccount() {
        return this.constructor
            .api()
            .action(this.id + "/send-account")
            .update([], false)
            .then(() => {
                this.is_account_mail_send = 1;
            });
    }

    /**
     *
     * @param start_date
     * @param end_date
     * @returns {*}
     */
    static dailyLeadsCount(start_date, end_date) {
        return this.api()
            .action("daily-count")
            .where("start_date", start_date)
            .where("end_date", end_date)
            .getSingle(false);
    }

    static dailyLeadsCountByCountry(start_date, end_date, countryId) {
        return Dataset.call("dossier/dossiers-for-country-leads-chart", {
            start_date: start_date,
            end_date: end_date,
            country_id: countryId,
        });
    }

    /**
     *
     * @returns {boolean|*}
     * @constructor
     */
    Customer() {
        return this.getRelated("customer");
    }

    /**
     *
     * @returns {boolean|*}
     */
    RevenueGroup() {
        return this.getRelated("revenueGroup");
    }

    /**
     *
     * @returns {boolean|*}
     */
    User() {
        return this.getRelated("user");
    }

    /**
     *
     * @returns {boolean|*}
     */
    LeadChannel() {
        return this.getRelated("leadChannel");
    }

    TravelInsuranceLabel() {
        if (this.travel_insurance_polis_number) {
            return "#" + this.travel_insurance_polis_number;
        }

        if (!this.travel_insurance_cid) {
            return null;
        }
        return constants.travelInsurance.find(
            (item) => item.id === this.travel_insurance_cid,
        ).label;
    }

    CancellationInsuranceLabel() {
        if (this.cancellation_insurance_polis_number) {
            return "#" + this.cancellation_insurance_polis_number;
        }

        if (!this.cancellation_insurance_cid) {
            return null;
        }
        return constants.travelInsurance.find(
            (item) => item.id === this.cancellation_insurance_cid,
        ).label;
    }

    /**
     *
     * @returns {boolean|*}
     */
    CancelReason() {
        return this.getRelated("cancelReason");
    }

    LeadOpportunity() {
        return constants.leadOpportunities.find(
            (opportunity) =>
                opportunity.id === parseInt(this.lead_opportunity_cid),
        );
    }

    /**
     *
     * @param onlyActive
     * @returns {*}
     */
    Proposals(onlyActive = true) {
        return orderBy(
            this.getRelated("proposals", function (proposal) {
                return !onlyActive || !proposal.IsArchived();
            }),
            "created_at",
            "desc",
        );
    }

    /**
     *
     * @returns {*}
     */
    BookedProposal() {
        return this.Proposals().find((proposal) =>
            proposal.IsBookingAccepted(),
        );
    }

    /**
     *
     * @returns {*}
     */
    Travellers() {
        return this.getRelated("travellers");
    }

    /**
     *
     * @returns {*}
     */
    ActiveTravellers() {
        return Traveller.query()
            .where("is_active", true)
            .where("dossier_id", this.id)
            .with("version")
            .get();
    }

    /**
     *
     * @returns {*}
     */
    Messages() {
        return this.getRelated("messages");
    }

    /**
     *
     * @returns {*}
     */
    DebitInvoices() {
        let invoices = [];
        this.Proposals(false).forEach((proposal) => {
            if (proposal.DebitInvoice() !== null) {
                invoices.push(proposal.DebitInvoice());
            }
        });

        return invoices;
    }

    CreditInvoices() {
        let invoices = [];
        this.Proposals(false).forEach((proposal) => {
            if (proposal.CreditInvoice() !== null) {
                invoices.push(proposal.CreditInvoice());
            }
        });

        return invoices;
    }

    /**
     *
     * @returns {*}
     */
    Lead() {
        return this.getRelated("lead");
    }

    /**
     *
     * @returns {boolean|*}
     */
    Country() {
        return this.getRelated("country");
    }

    /**
     *
     * @returns {Array}
     */
    Payments() {
        return orderBy(this.getRelated("payments"), "paid_at");
    }

    /**
     *
     * @param leadOpportunityCid
     * @returns {T}
     */
    LeadOpportunityById(leadOpportunityCid) {
        return constants.leadOpportunities.find(
            (opporunity) => opporunity.id == leadOpportunityCid,
        );
    }

    /**
     *
     * @param onlyActive
     * @returns {boolean}
     */
    HasBookingAcceptedProposals(onlyActive = true) {
        return (
            this.Proposals(onlyActive).filter((proposal) =>
                proposal.IsBookingAccepted(),
            ).length > 0
        );
    }

    /**
     *
     * @param onlyActive
     * @returns {boolean}
     */
    HasEmployeeAcceptedProposals(onlyActive = true) {
        return (
            this.Proposals(onlyActive).filter((proposal) =>
                proposal.IsEmployeeAccepted(),
            ).length > 0
        );
    }

    /**
     *
     * @param onlyActive
     * @returns {boolean}
     */
    HasSendProposals(onlyActive = true) {
        return (
            this.Proposals(onlyActive).filter((proposal) =>
                proposal.IsSendToCustomer(),
            ).length > 0
        );
    }

    LatestSentProposal(onlyActive = true) {
        return this.Proposals(onlyActive).find((proposal) =>
            proposal.IsSendToCustomer(),
        );
    }

    AreRequiredConditionsAccepted() {
        return (
            this.company_conditions_accepted_at !== null &&
            this.anvr_conditions_accepted_at !== null
        );
    }

    AreInsurancesDefined() {
        return (
            this.travel_insurance_cid !== null &&
            this.cancellation_insurance_cid !== null
        );
    }

    AreTravellersValid() {
        const travellers = Traveller.query()
            .where("dossier_id", this.id)
            .with("version")
            .get();

        return travellers.every(
            (traveller) =>
                !traveller.is_active ||
                (traveller.version.date_of_birth != null &&
                    traveller.version.gender_cid != null),
        );
    }

    /**
     *
     * @returns {boolean}
     */
    IsCancelled() {
        return this.cancelled_at !== null;
    }

    /**
     *
     * @returns {*}
     */
    IsCancellationConfirmed() {
        return this.is_cancellation_confirmed;
    }

    TotalPaid() {
        return Object.values(this.Payments()).reduce(function (sum, payment) {
            return sum + parseFloat(payment.amount);
        }, 0);
    }

    /**
     *
     * @returns {*}
     */
    CreditInvoiceMismatches() {
        return this.getRelated("creditMismatches");
    }

    /**
     *
     * @returns {*}
     */
    DebitInvoiceMismatches() {
        return this.getRelated("debitMismatches");
    }

    static getUncheckedAgentProductsCount() {
        return this.api().action("unchecked_agent_products").getValue();
    }
    static getUncheckedAgentAttachmentDownloadsCount() {
        return this.api()
            .action("unchecked_agent_attachment_downloads")
            .getValue();
    }
}
